import { FC, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react'

import MentionPopup from './MentionPopup'

type THoveredMention = {
  username: string
  top: number
  left: number
  tunnelTop: number
  tunnelLeft: number
  tunnelWidth: number
  tunnelHeight: number
}

type HoverMentionPopupProps = {
  children: ReactNode
}

const mentionSelector = 'a.u-url.mention.status-link'
const popupWidthPx = 360
const horizontalPaddingPx = 12
const popupTopOffsetPx = 8
const tunnelPaddingPx = 8
const tunnelTopOffsetPx = 2
const minTunnelHeightPx = 8
const tunnelClassName = 'absolute z-[5] bg-transparent'
const popupClassName = 'absolute z-[6]'

const getMentionUsernameFromHref = (mention: HTMLAnchorElement) => {
  const href = mention.getAttribute('href') || ''
  const sanitizedHref = href.split('#')[0].split('?')[0]
  const slugs = sanitizedHref.split('/').filter(Boolean)
  const lastSlug = slugs.at(-1) || ''
  return decodeURIComponent(lastSlug.replace(/^@/, ''))
}

const HoverMentionPopup: FC<HoverMentionPopupProps> = ({ children }) => {
  const contentWrapperRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPointerInPopupRef = useRef(false)
  const [hoveredMention, setHoveredMention] = useState<THoveredMention | null>(
    null,
  )

  const clearCloseTimeout = () => {
    if (!closeTimeoutRef.current) return
    clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = null
  }

  const schedulePopupClose = () => {
    clearCloseTimeout()

    closeTimeoutRef.current = setTimeout(() => {
      if (isPointerInPopupRef.current) return
      setHoveredMention(null)
      closeTimeoutRef.current = null
    }, 500)
  }

  const setMentionFromAnchor = (mention: HTMLAnchorElement) => {
    const wrapper = contentWrapperRef.current
    if (!wrapper) return

    const username = getMentionUsernameFromHref(mention)
    if (!username) return

    const mentionRect = mention.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    const popupWidth = popupWidthPx
    const horizontalPadding = horizontalPaddingPx
    const tunnelPadding = tunnelPaddingPx
    const maxLeft = Math.max(
      horizontalPadding,
      wrapper.clientWidth - popupWidth - horizontalPadding,
    )
    const rawLeft = mentionRect.left - wrapperRect.left
    const popupLeft = Math.min(Math.max(rawLeft, horizontalPadding), maxLeft)
    const popupTop = mentionRect.bottom - wrapperRect.top + popupTopOffsetPx
    const mentionLeft = mentionRect.left - wrapperRect.left
    const mentionRight = mentionRect.right - wrapperRect.left
    const tunnelLeft = Math.min(mentionLeft, popupLeft) - tunnelPadding
    const tunnelRight =
      Math.max(mentionRight, popupLeft + popupWidth) + tunnelPadding
    const tunnelTop = mentionRect.bottom - wrapperRect.top - tunnelTopOffsetPx
    const tunnelHeight = Math.max(
      minTunnelHeightPx,
      popupTop - tunnelTop + tunnelTopOffsetPx,
    )

    clearCloseTimeout()
    setHoveredMention({
      username,
      top: popupTop,
      left: popupLeft,
      tunnelTop,
      tunnelLeft,
      tunnelWidth: tunnelRight - tunnelLeft,
      tunnelHeight,
    })
  }

  const handleMouseOver = (evt: MouseEvent<HTMLDivElement>) => {
    const target = evt.target as HTMLElement | null
    const mention = target?.closest(mentionSelector) as HTMLAnchorElement | null
    if (!mention) return
    setMentionFromAnchor(mention)
  }

  const handleMouseOut = (evt: MouseEvent<HTMLDivElement>) => {
    const target = evt.target as HTMLElement | null
    const mention = target?.closest(mentionSelector) as HTMLAnchorElement | null
    if (!mention) return

    const relatedTarget = evt.relatedTarget as Node | null
    if (relatedTarget && mention.contains(relatedTarget)) return
    if (relatedTarget && popupRef.current?.contains(relatedTarget)) return

    const nextMention =
      relatedTarget instanceof Element
        ? relatedTarget.closest(mentionSelector)
        : null

    if (nextMention instanceof HTMLAnchorElement) {
      setMentionFromAnchor(nextMention)
      return
    }

    schedulePopupClose()
  }

  useEffect(
    () => () => {
      clearCloseTimeout()
    },
    [],
  )

  return (
    <div
      ref={contentWrapperRef}
      className="relative"
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseLeave={schedulePopupClose}
    >
      {children}
      {hoveredMention && (
        <div
          className={tunnelClassName}
          style={{
            top: hoveredMention.tunnelTop,
            left: hoveredMention.tunnelLeft,
            width: hoveredMention.tunnelWidth,
            height: hoveredMention.tunnelHeight,
          }}
          onMouseEnter={clearCloseTimeout}
          onMouseLeave={schedulePopupClose}
        />
      )}
      {hoveredMention && (
        <div
          ref={popupRef}
          className={popupClassName}
          style={{ top: hoveredMention.top, left: hoveredMention.left }}
          onMouseEnter={() => {
            isPointerInPopupRef.current = true
            clearCloseTimeout()
          }}
          onMouseLeave={() => {
            isPointerInPopupRef.current = false
            schedulePopupClose()
          }}
        >
          <MentionPopup username={hoveredMention.username} />
        </div>
      )}
    </div>
  )
}

export default HoverMentionPopup
